import { SYSTEM_MODES } from '@silverfort-nodelibs/common/utils/system-mode-config';
import { FEATURE_FLAGS_NAMES } from '@silverfort-nodelibs/common/utils/consts';

import useFeature from '../featureFlags/useFeature';
import {
    PricingModulesStatus,
    SYSTEM_MODE,
    usePricingModulesState
} from '../../../../context/contexts/PricingModuleContext';
import { useMemo } from 'react';
import { DisabledDisplayOptions } from './types';
import { HIDDEN_FIELDS_CONFIG } from './hiddenFieldsConfigs';
import { HiddenLockedFeatureDisplayConfig, LockedFeatureDisplayConfig } from '../../../common/LockedFeatures';
import { getDisplay } from './utils';
import * as FIELDS from '../../common/fields';
import { isFieldHiddenBySystemMode } from '../../common/utils';

const getFieldDisplay = (
    fieldId: string,
    pricingModulesState: PricingModulesStatus,
    mode: SYSTEM_MODE
): LockedFeatureDisplayConfig => {
    return getDisplay(fieldId, pricingModulesState, mode, HIDDEN_FIELDS_CONFIG, isFieldHiddenBySystemMode);
};

export const buildFieldsToDisplay = (
    fieldIds: string[],
    pricingModulesState: PricingModulesStatus,
    mode: SYSTEM_MODE
) => {
    return fieldIds.filter((fieldId) => {
        const fieldDisplayConfig = getFieldDisplay(fieldId, pricingModulesState, mode);
        return (
            fieldDisplayConfig.enabled ||
            fieldDisplayConfig.disabledDisplay === DisabledDisplayOptions.TOOLTIP ||
            fieldDisplayConfig.disabledDisplay === DisabledDisplayOptions.SHOWSTOPPER
        );
    });
};

export const useGetFieldDisplay = (field: string) => {
    const systemMode: SYSTEM_MODE | undefined = useFeature(FEATURE_FLAGS_NAMES.SystemMode);
    const { pricingModulesState } = usePricingModulesState();
    const fieldDisplay = useMemo(() => {
        if (pricingModulesState) {
            return getFieldDisplay(field, pricingModulesState, systemMode ?? SYSTEM_MODES.ON_PREM);
        }
        const hiddenDisplayConfig: HiddenLockedFeatureDisplayConfig = {
            enabled: false,
            disabledDisplay: DisabledDisplayOptions.HIDDEN
        };
        return hiddenDisplayConfig;
    }, [field, pricingModulesState, systemMode]);
    return fieldDisplay;
};

export const useShouldDisplayFields = (fieldIds: string[]) => {
    const systemMode: SYSTEM_MODE | undefined = useFeature(FEATURE_FLAGS_NAMES.SystemMode);
    const { pricingModulesState } = usePricingModulesState();
    const fieldsToDisplay = useMemo(() => {
        if (pricingModulesState) {
            return buildFieldsToDisplay(fieldIds, pricingModulesState, systemMode ?? SYSTEM_MODES.ON_PREM);
        }
        return [];
    }, [fieldIds, pricingModulesState, systemMode]);
    return fieldsToDisplay;
};

export const useGetFieldsTermsToHide = (fieldIds?: string[]) => {
    const systemMode: SYSTEM_MODE | undefined = useFeature(FEATURE_FLAGS_NAMES.SystemMode);
    const { pricingModulesState } = usePricingModulesState();
    const fieldsTermsToHide = useMemo(() => {
        if (pricingModulesState) {
            const fieldsToCheck = getFlatFields(fieldIds || Object.keys(HIDDEN_FIELDS_CONFIG));
            const fieldsToShow = buildFieldsToDisplay(
                fieldsToCheck.map((field) => field.ID),
                pricingModulesState,
                systemMode ?? SYSTEM_MODES.ON_PREM
            );
            const termsToHide = fieldsToCheck
                .filter((field) => !fieldsToShow.includes(field.ID))
                .map((field) => [field.NAME, ...(field.TAGS || [])])
                .flat();
            return termsToHide;
        }
        return [];
    }, [fieldIds, pricingModulesState, systemMode]);
    return fieldsTermsToHide;
};

const getFlatFields = (fieldIds?: string[]) => {
    const fieldsArray = Object.values(FIELDS)
        .map((fieldsSet) => Object.values(fieldsSet))
        .flat();

    if (fieldIds) {
        return fieldsArray.filter((field) => fieldIds.includes(field.ID));
    }

    return fieldsArray;
};
